import { useTranslate } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { CustomLayoutDrawerContent } from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { StyledForm, StyledItem } from 'module/ui/components/atoms/form/form';
import { Form } from 'module/ui/components/atoms/form/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Input } from 'module/ui/components/atoms/input/input';
import { Radio } from 'module/ui/components/atoms/radio';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { Drawer, DrawerTitle } from '@rocketlane/ui';
import { Space } from 'antd';
import { SignalType } from 'api/signal/types';
import { ViewAvailability } from 'api/views/types';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FlexWrapperWithJustifyBetween } from 'components/common/styles';
import {
  DrawerFooterButton,
  DrawerFooterButtonsContainer,
  DrawerHeaderTitle,
  FormContainer,
  FormSection,
} from './styles';

interface View {
  viewName: string;
  filter: GroupedFilter;
  signalType?: SignalType;
  myAccountSignalType?: SignalType;
  selectedSignals?: string[];
  self?: boolean;
}

interface ManageViewDrawerProps {
  isDrawerVisible: boolean;
  onClose: () => void;
  editingView: View | null;
  onSave: (updatedView: View) => void;
  onUpdateView?: (view: View) => Promise<void>;
  filter: GroupedFilter;
}

export const ManageViewDrawer = ({
  isDrawerVisible,
  onClose,
  editingView,
  onSave,
  onUpdateView,
  filter,
}: ManageViewDrawerProps) => {
  const [editedViewName, setEditedViewName] = useState<string>();
  const [form] = Form.useForm();
  const translate = useTranslate();
  const isEditMode = !!editingView;
  //TODO: Update this to check if the user has access to share views
  const hasAccessToShareViews = false;

  useEffect(() => {
    if (editingView?.viewName) {
      setEditedViewName(editingView?.viewName);
      form.setFieldsValue({
        viewName: editingView.viewName,
        editViewAvailability: editingView.self
          ? ViewAvailability.OnlyMe
          : ViewAvailability.AllTeamMembers,
      });
    } else {
      setEditedViewName('');
      form.setFieldsValue({
        viewName: '',
        editViewAvailability: ViewAvailability.OnlyMe,
      });
    }
  }, [editingView, form]);

  const handleSaveView = async () => {
    const formValues = form.getFieldsValue();

    try {
      const viewToSave: View = {
        viewName: formValues.viewName,
        filter: filter as any,
        self: formValues.editViewAvailability === ViewAvailability.OnlyMe,
      };

      if (isEditMode && onUpdateView && editingView) {
        const updatedView: View = {
          ...editingView,
          viewName: formValues.viewName,
          filter: filter as any,
          self: formValues.editViewAvailability === ViewAvailability.OnlyMe,
        };

        await onUpdateView(updatedView);
      }
      onSave(viewToSave);
    } catch (error) {
      logger.error('Error saving view', { error });
    }
    onClose();
  };

  const onChange = useCallback((changedValues: any, allValues: any) => {
    setEditedViewName(allValues.viewName);
  }, []);

  return (
    <Drawer
      open={isDrawerVisible}
      onClose={onClose}
      size={360}
      placement="right"
    >
      <DrawerTitle className="sr-only">
        {isEditMode
          ? translate('views.edit_view')
          : translate('views.create_view')}
      </DrawerTitle>
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          <FlexWrapperWithJustifyBetween>
            <DrawerHeaderTitle>
              {isEditMode
                ? translate('views.edit_view')
                : translate('views.create_view')}
            </DrawerHeaderTitle>
          </FlexWrapperWithJustifyBetween>
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <StyledForm layout="vertical" form={form} onValuesChange={onChange}>
            <FormContainer>
              <StyledItem
                label={<FormattedMessage id="views.view_name.label" />}
                name="viewName"
              >
                <Input
                  placeholder={isEditMode ? undefined : 'E.g. Open Bugs'}
                />
              </StyledItem>
            </FormContainer>

            <FormSection>
              <StyledItem
                label={<FormattedMessage id="make_this_view_available_for" />}
                name="editViewAvailability"
              >
                <Radio.Group
                  onChange={(e) =>
                    form.setFieldsValue({
                      editViewAvailability: e.target.value,
                    })
                  }
                >
                  <Space direction="vertical">
                    <Radio
                      value={ViewAvailability.OnlyMe}
                      disabled={!hasAccessToShareViews}
                    >
                      <FormattedMessage id="only_me" />
                    </Radio>
                    <Radio
                      value={ViewAvailability.AllTeamMembers}
                      disabled={!hasAccessToShareViews}
                    >
                      <FormattedMessage id="all_team_members" />
                    </Radio>
                  </Space>
                </Radio.Group>
              </StyledItem>
            </FormSection>
          </StyledForm>
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer>
          <DrawerFooterButtonsContainer>
            <DrawerFooterButton onClick={onClose}>
              {translate('cancel')}
            </DrawerFooterButton>
            <DrawerFooterButton
              variant="primary"
              leftIcon={<FontAwesomeIcon icon={faSave} size={12} />}
              onClick={handleSaveView}
              disabled={isEmpty(editedViewName)}
            >
              {isEditMode ? translate('update') : translate('save')}
            </DrawerFooterButton>
          </DrawerFooterButtonsContainer>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </Drawer>
  );
};
